import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import {
  DEFAULT_ZONE_SETS,
  zoneSetKeyFor,
  unitLabelKeyFor,
  SportType,
  RunIntensity,
  BikeIntensity,
  IntensityTarget,
  ZoneSetKey,
  ZoneDefinition,
  WorkoutSegment,
} from '@/types';
import { validateZones, zonesEqual, type ZoneValidationResult } from '@/shared/utils/zoneValidation';
import { loadUserZones, saveUserZones, type ZoneOverrides } from '@/features/user-zones';

const INITIAL_PROMPT = '';
const DEFAULT_BIKE_PROMPT = '60 min bike workout: 10 min warmup, 3x8 min tempo with 4 min recovery, 10 min cooldown';
const DEFAULT_BIKE_SEGMENTS: WorkoutSegment[] = [
  { time: 0,    duration: 600, powerLow: 0.45, powerHigh: 0.50, type: 'Warmup',     text: 'Warmup' },
  { time: 600,  duration: 480, powerLow: 0.85, powerHigh: 0.85, type: 'IntervalsT', text: 'Tempo 8 min' },
  { time: 1080, duration: 240, powerLow: 0.50, powerHigh: 0.50, type: 'IntervalsT', text: 'Recovery 4 min' },
  { time: 1320, duration: 480, powerLow: 0.85, powerHigh: 0.85, type: 'IntervalsT', text: 'Tempo 8 min' },
  { time: 1800, duration: 240, powerLow: 0.50, powerHigh: 0.50, type: 'IntervalsT', text: 'Recovery 4 min' },
  { time: 2040, duration: 480, powerLow: 0.85, powerHigh: 0.85, type: 'IntervalsT', text: 'Tempo 8 min' },
  { time: 2520, duration: 600, powerLow: 0.40, powerHigh: 0.45, type: 'Cooldown',   text: 'Cooldown' },
];

/** Cloned in a `useState` initializer so user edits never mutate the module-level default arrays. */
function freshDefaultZoneSets(): Record<ZoneSetKey, ZoneDefinition[]> {
  return {
    'bike-power': DEFAULT_ZONE_SETS['bike-power'].map((z) => ({ ...z })),
    'bike-hr':    DEFAULT_ZONE_SETS['bike-hr'].map((z) => ({ ...z })),
    'run-pace':   DEFAULT_ZONE_SETS['run-pace'].map((z) => ({ ...z })),
    'run-power':  DEFAULT_ZONE_SETS['run-power'].map((z) => ({ ...z })),
    'run-hr':     DEFAULT_ZONE_SETS['run-hr'].map((z) => ({ ...z })),
  };
}

/**
 * Single source of truth for every workout-generator setting:
 *   - sport tab + per-sport intensity target (bike: power|hr, run: pace|hr|power)
 *   - prompt text per sport
 *   - zone tables keyed by `ZoneSetKey`
 *   - validation flags (gates generate/export buttons + colours invalid inputs red)
 *   - "Custom vs Default" detection (drives the method-name badge)
 *   - Supabase load/save of the active zone set
 *
 * The hook intentionally returns BOTH the active zones (memoized) and a setter focused on the
 * active set so consumers can stay sport-agnostic — no need to branch on `sport` at the call site.
 */
export function useWorkoutSettings(user?: User | null) {
  const [sport, setSport] = useState<SportType>('bike');
  const [bikeIntensity, setBikeIntensity] = useState<BikeIntensity>('power');
  const [runIntensity, setRunIntensity] = useState<RunIntensity>('pace');
  const [prompts, setPrompts] = useState({ bike: INITIAL_PROMPT, run: INITIAL_PROMPT });
  const [zoneSets, setZoneSets] = useState<Record<ZoneSetKey, ZoneDefinition[]>>(freshDefaultZoneSets);
  /**
   * Mirrors which keys were loaded from Supabase so the UI knows whether the badge should read
   * "Custom" purely because of in-memory edits OR because the user has persisted overrides.
   */
  const [savedOverrides, setSavedOverrides] = useState<ZoneOverrides>({});
  const [saving, setSaving] = useState(false);
  /** Reset zoneSets when user logs out — prevents lingering edits leaking into a new session. */
  const lastUserIdRef = useRef<string | null>(null);

  // Effective intensity target for the active sport.
  const activeIntensity: IntensityTarget = sport === 'bike' ? bikeIntensity : runIntensity;
  const activeZoneSetKey: ZoneSetKey = zoneSetKeyFor(sport, activeIntensity);
  const activeZones = zoneSets[activeZoneSetKey];
  const unitLabelKey = unitLabelKeyFor(sport, activeIntensity);

  // Detect "Custom (Własne)" by deep-equality vs the shipped defaults. Memoized to avoid
  // re-running the array walk on every re-render.
  const isCustomActive = useMemo(
    () => !zonesEqual(activeZones, DEFAULT_ZONE_SETS[activeZoneSetKey]),
    [activeZones, activeZoneSetKey],
  );

  const validation: ZoneValidationResult = useMemo(() => validateZones(activeZones), [activeZones]);

  // Method label shown in the small badge — switches to "Custom" as soon as ANY value drifts
  // from the shipped default table. The badge text itself is resolved by the UI from i18n.
  const activeMethodLabel = isCustomActive ? null : activeZones[0]?.method ?? null;

  /** Update zones for an arbitrary key (used by tests / future "Reset" affordance). */
  const setZonesForKey = useCallback((key: ZoneSetKey, updater: ZoneDefinition[] | ((prev: ZoneDefinition[]) => ZoneDefinition[])) => {
    setZoneSets((prev) => ({
      ...prev,
      [key]: typeof updater === 'function' ? (updater as (p: ZoneDefinition[]) => ZoneDefinition[])(prev[key]) : updater,
    }));
  }, []);

  /** Convenience setter scoped to the currently active set — what `ZonesDisplay` actually calls. */
  const setActiveZones = useCallback(
    (updater: ZoneDefinition[] | ((prev: ZoneDefinition[]) => ZoneDefinition[])) => {
      setZonesForKey(activeZoneSetKey, updater);
    },
    [setZonesForKey, activeZoneSetKey],
  );

  /** Patch a single zone's lower/upper bound — the most common edit ZonesDisplay emits. */
  const updateZoneBound = useCallback(
    (zoneId: string, field: 'min' | 'max', value: number) => {
      setActiveZones((prev) => prev.map((z) => (z.id === zoneId ? { ...z, [field]: value } : z)));
    },
    [setActiveZones],
  );

  // Legacy compatibility — App.tsx / components still expect `bikeZones` / `runZones` for the
  // chart and CYCLE history loader. We keep those props but they now READ FROM the per-key map.
  const bikeZones = sport === 'bike' ? activeZones : zoneSets[zoneSetKeyFor('bike', bikeIntensity)];
  const runZones = sport === 'run'  ? activeZones : zoneSets[zoneSetKeyFor('run', runIntensity)];

  /** Legacy `setBikeZones` — preserved for any caller still passing it; targets the active bike key. */
  const setBikeZones = useCallback(
    (updater: ZoneDefinition[] | ((prev: ZoneDefinition[]) => ZoneDefinition[])) => {
      setZonesForKey(zoneSetKeyFor('bike', bikeIntensity), updater);
    },
    [setZonesForKey, bikeIntensity],
  );

  /** Legacy `setRunZones` — preserved for callers; targets the active run key. */
  const setRunZones = useCallback(
    (updater: ZoneDefinition[] | ((prev: ZoneDefinition[]) => ZoneDefinition[])) => {
      setZonesForKey(zoneSetKeyFor('run', runIntensity), updater);
    },
    [setZonesForKey, runIntensity],
  );

  // Hydrate zones from Supabase on login. The effect runs once per user transition; logout
  // resets back to shipped defaults so a different user logging in doesn't see the previous
  // user's customised set.
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (lastUserIdRef.current === currentUserId) return;
    lastUserIdRef.current = currentUserId;

    if (!currentUserId) {
      setZoneSets(freshDefaultZoneSets());
      setSavedOverrides({});
      return;
    }

    let cancelled = false;
    void (async () => {
      const overrides = await loadUserZones(currentUserId);
      if (cancelled) return;
      setSavedOverrides(overrides);
      setZoneSets((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(overrides) as ZoneSetKey[]) {
          const saved = overrides[key];
          if (saved && saved.length > 0) next[key] = saved.map((z) => ({ ...z }));
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  /**
   * Persist the CURRENT (active) zones table for the logged-in user. Returns the operation
   * outcome so the caller can show a toast and re-lock the button on failure.
   */
  const saveActiveZones = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (!user?.id) return { ok: false, error: 'no-user' };
    if (!validation.isValid) return { ok: false, error: 'invalid' };

    setSaving(true);
    try {
      const result = await saveUserZones(user.id, activeZoneSetKey, activeZones);
      if (result.ok) {
        setSavedOverrides((prev) => ({
          ...prev,
          [activeZoneSetKey]: activeZones.map((z) => ({ ...z })),
        }));
      }
      return result;
    } finally {
      setSaving(false);
    }
  }, [user?.id, validation.isValid, activeZoneSetKey, activeZones]);

  /** True when the user has BOTH an authenticated session AND a valid, modified zone set to save. */
  const canSave = !!user?.id && validation.isValid && isCustomActive;

  const currentPrompt = prompts[sport];

  return {
    // sport + intensity
    sport,
    setSport,
    runIntensity,
    setRunIntensity,
    bikeIntensity,
    setBikeIntensity,
    activeIntensity,
    // prompts
    prompts,
    setPrompts,
    currentPrompt,
    INITIAL_PROMPT,
    DEFAULT_BIKE_PROMPT,
    DEFAULT_BIKE_SEGMENTS,
    // zones
    zoneSets,
    activeZoneSetKey,
    activeZones,
    setActiveZones,
    updateZoneBound,
    unitLabelKey,
    isCustomActive,
    activeMethodLabel,
    validation,
    // persistence
    saveActiveZones,
    saving,
    canSave,
    savedOverrides,
    // legacy (preserved for unchanged callers)
    bikeZones,
    runZones,
    setBikeZones,
    setRunZones,
  };
}
